import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { ScoreBadge } from "@/components/atomic-crm/scoring/ScoreBadge";

export function UnassignedQueue() {
    const [contacts, setContacts] = useState<any[]>([]);
    const [teamMembers, setTeamMembers] = useState<Array<{ id: number; first_name: string; last_name: string }>>([]);
    const [assigningId, setAssigningId] = useState<number | null>(null);
    const [selectedMember, setSelectedMember] = useState("");

    useEffect(() => {
        supabase.from("contacts")
            .select("id, first_name, last_name, title, current_score, current_tier, companies(name)")
            .is("owner_id", null)
            .order("current_score", { ascending: false, nullsFirst: false })
            .then(({ data }) => { if (data) setContacts(data); });

        supabase.from("sales").select("id, first_name, last_name").eq("disabled", false)
            .then(({ data }) => { if (data) setTeamMembers(data); });
    }, []);

    const handleAssign = async (contactId: number, memberId: number) => {
        await supabase.from("contacts").update({ owner_id: memberId }).eq("id", contactId);
        setContacts((prev) => prev.filter((c) => c.id !== contactId));
        setAssigningId(null);
        setSelectedMember("");
    };

    const handleClaim = async (contactId: number) => {
        // Get current user's sales ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: sale } = await supabase.from("sales").select("id").eq("user_id", user.id).single();
        if (sale) await handleAssign(contactId, sale.id);
    };

    const handleRoundRobin = async () => {
        if (teamMembers.length === 0 || contacts.length === 0) return;
        for (let i = 0; i < contacts.length; i++) {
            const member = teamMembers[i % teamMembers.length];
            await supabase.from("contacts").update({ owner_id: member.id }).eq("id", contacts[i].id);
        }
        setContacts([]);
    };

    if (contacts.length === 0) return null;

    return (
        <div className="rounded-md border">
            <div className="flex items-center justify-between border-b p-3">
                <h3 className="font-semibold text-sm">Unassigned Contacts ({contacts.length})</h3>
                <Button variant="outline" size="sm" onClick={handleRoundRobin}>Round Robin Assign</Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {contacts.slice(0, 20).map((c) => (
                        <TableRow key={c.id}>
                            <TableCell className="font-medium">{c.first_name} {c.last_name}</TableCell>
                            <TableCell className="text-muted-foreground">{c.companies?.name}</TableCell>
                            <TableCell><ScoreBadge score={c.current_score} tier={c.current_tier} /></TableCell>
                            <TableCell className="text-right">
                                {assigningId === c.id ? (
                                    <div className="flex items-center gap-1 justify-end">
                                        <Select value={selectedMember} onValueChange={setSelectedMember}>
                                            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent>
                                                {teamMembers.map((m) => (
                                                    <SelectItem key={m.id} value={String(m.id)}>{m.first_name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={!selectedMember}
                                            onClick={() => handleAssign(c.id, parseInt(selectedMember))}>OK</Button>
                                    </div>
                                ) : (
                                    <div className="flex gap-1 justify-end">
                                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleClaim(c.id)}>Claim</Button>
                                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAssigningId(c.id)}>Assign</Button>
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
